import React, { useEffect, useState } from 'react';
import { useTheme } from '../../../../Context/ThemeContext';
import { AdministratorHandler } from '../AdministratorHandler';

interface Role {
    id?: string;
    name?: string;
}

interface FormData {
    userName: string;
    firstName: string;
    lastName: string;
    emailAddress: string;
    phoneNumber: string;
    accessCards: string[];
    adminStatus: string;
    isSeniorAdmin: boolean;
}

interface FormErrors {
    userName?: string;
    firstName?: string;
    emailAddress?: string;
    accessCards?: string;
    adminStatus?: string;
}

interface AdministratorDetailFormProps {
    onSuccess?: () => void;
    onCancel?: () => void;
}

const AdministratorDetailForm: React.FC<AdministratorDetailFormProps> = ({
    onSuccess,
    onCancel,
}) => {
    const { theme, isMobile } = useTheme();
    const [loading, setLoading] = useState<boolean>(false);
    const [rolesLoading, setRolesLoading] = useState<boolean>(true);
    const [roles, setRoles] = useState<Role[]>([]);
    const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [errors, setErrors] = useState<FormErrors>({});
    const [rolesDropdownOpen, setRolesDropdownOpen] = useState<boolean>(false);

    const [formData, setFormData] = useState<FormData>({
        userName: '',
        firstName: '',
        lastName: '',
        emailAddress: '',
        phoneNumber: '',
        accessCards: [],
        adminStatus: 'Active',
        isSeniorAdmin: false,
    });

    const handler = new AdministratorHandler();

    useEffect(() => {
        const loadRoles = async () => {
            try {
                const response = await handler.getRoles();
                setRoles(response.results || []);
            } catch (error) {
                console.error('Error loading roles:', error);
                setNotification({ type: 'error', message: 'Failed to load roles.' });
            } finally {
                setRolesLoading(false);
            }
        };
        loadRoles();
    }, []);

    // Auto-dismiss notifications
    useEffect(() => {
        if (notification) {
            const timer = setTimeout(() => setNotification(null), 5000);
            return () => clearTimeout(timer);
        }
    }, [notification]);

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.userName.trim()) {
            newErrors.userName = 'Username is required';
        }

        if (!formData.firstName.trim()) {
            newErrors.firstName = 'First name is required';
        }

        if (!formData.emailAddress.trim()) {
            newErrors.emailAddress = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.emailAddress)) {
            newErrors.emailAddress = 'Please enter a valid email';
        }

        if (formData.accessCards.length === 0) {
            newErrors.accessCards = 'Please select at least one role';
        }

        if (!formData.adminStatus) {
            newErrors.adminStatus = 'Please select admin status';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field: keyof FormData, value: string | boolean | string[]) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        // Clear error when user starts typing
        if (errors[field as keyof FormErrors]) {
            setErrors(prev => ({ ...prev, [field]: undefined }));
        }
    };

    const toggleRole = (roleId: string | undefined) => {
        if (!roleId) return;
        const newRoles = formData.accessCards.includes(roleId)
            ? formData.accessCards.filter(id => id !== roleId)
            : [...formData.accessCards, roleId];
        handleInputChange('accessCards', newRoles);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        try {
            await handler.addAdminUser({
                userName: formData.userName,
                firstName: formData.firstName,
                lastName: formData.lastName,
                emailAddress: formData.emailAddress,
                phoneNumber: formData.phoneNumber,
                accessCards: formData.accessCards,
                adminStatus: formData.adminStatus,
                isSeniorAdmin: formData.isSeniorAdmin,
            } as any);

            setNotification({ type: 'success', message: 'Administrator added successfully!' });

            // Reset form
            setFormData({
                userName: '',
                firstName: '',
                lastName: '',
                emailAddress: '',
                phoneNumber: '',
                accessCards: [],
                adminStatus: 'Active',
                isSeniorAdmin: false,
            });

            onSuccess?.();
        } catch (error) {
            console.error('Error adding admin:', error);
            setNotification({ type: 'error', message: 'Failed to add administrator.' });
        } finally {
            setLoading(false);
        }
    };

    const styles: Record<string, React.CSSProperties> = {
        container: {
            padding: 0,
        },
        header: {
            marginBottom: '24px',
        },
        title: {
            fontSize: isMobile ? '24px' : '28px',
            fontWeight: 700,
            color: theme.titleColor,
            marginBottom: '8px',
        },
        subtitle: {
            fontSize: '14px',
            color: theme.textSecondary,
            marginBottom: '16px',
        },
        card: {
            background: theme.card,
            borderRadius: '8px',
            padding: isMobile ? '16px' : '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
        formGrid: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: '20px',
        },
        formGroup: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '6px',
        },
        formGroupFull: {
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '6px',
            gridColumn: isMobile ? '1' : '1 / -1',
        },
        label: {
            fontSize: '14px',
            fontWeight: 600,
            color: theme.text,
        },
        required: {
            color: '#ef4444',
            marginLeft: '4px',
        },
        input: {
            padding: '12px 14px',
            fontSize: '14px',
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: '6px',
            background: theme.inputBg,
            color: theme.text,
            outline: 'none',
            transition: 'border-color 0.2s, box-shadow 0.2s',
        },
        inputError: {
            borderColor: '#ef4444',
        },
        select: {
            padding: '12px 14px',
            fontSize: '14px',
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: '6px',
            background: theme.inputBg,
            color: theme.text,
            outline: 'none',
            cursor: 'pointer',
            appearance: 'none' as const,
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239ca3af' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'right 12px center',
            paddingRight: '36px',
        },
        errorText: {
            fontSize: '12px',
            color: '#ef4444',
            marginTop: '4px',
        },
        multiSelectContainer: {
            position: 'relative' as const,
        },
        multiSelectTrigger: {
            padding: '12px 14px',
            fontSize: '14px',
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: '6px',
            background: theme.inputBg,
            color: theme.text,
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            minHeight: '46px',
            flexWrap: 'wrap' as const,
            gap: '6px',
        },
        selectedTag: {
            background: '#2563eb',
            color: '#fff',
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
        },
        tagRemove: {
            cursor: 'pointer',
            fontSize: '14px',
            lineHeight: 1,
        },
        dropdown: {
            position: 'absolute' as const,
            top: '100%',
            left: 0,
            right: 0,
            marginTop: '4px',
            background: theme.card,
            border: `1px solid ${theme.border}`,
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 100,
            maxHeight: '200px',
            overflowY: 'auto' as const,
        },
        dropdownItem: {
            padding: '10px 14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            transition: 'background 0.2s',
        },
        checkbox: {
            width: '18px',
            height: '18px',
            borderRadius: '4px',
            border: `2px solid ${theme.inputBorder}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s',
        },
        checkboxChecked: {
            background: '#2563eb',
            borderColor: '#2563eb',
        },
        switchContainer: {
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
        },
        switch: {
            width: '48px',
            height: '26px',
            borderRadius: '13px',
            position: 'relative' as const,
            cursor: 'pointer',
            transition: 'background 0.3s',
        },
        switchThumb: {
            width: '22px',
            height: '22px',
            borderRadius: '50%',
            background: '#fff',
            position: 'absolute' as const,
            top: '2px',
            transition: 'left 0.3s',
            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        },
        buttonGroup: {
            display: 'flex',
            gap: '12px',
            marginTop: '24px',
            flexWrap: 'wrap' as const,
        },
        submitButton: {
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: 600,
            background: '#22c55e',
            color: '#fff',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'background 0.2s',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
        },
        cancelButton: {
            padding: '12px 24px',
            fontSize: '14px',
            fontWeight: 600,
            background: 'transparent',
            color: theme.text,
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: '6px',
            cursor: 'pointer',
            transition: 'all 0.2s',
        },
        notification: {
            padding: '12px 16px',
            borderRadius: '6px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            animation: 'fadeIn 0.3s ease',
        },
        loadingOverlay: {
            position: 'fixed' as const,
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        loadingCard: {
            background: theme.card,
            padding: '32px 48px',
            borderRadius: '12px',
            display: 'flex',
            flexDirection: 'column' as const,
            alignItems: 'center',
            gap: '16px',
        },
        spinner: {
            width: '40px',
            height: '40px',
            border: '4px solid rgba(37, 99, 235, 0.2)',
            borderTopColor: '#2563eb',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
        },
    };

    if (rolesLoading) {
        return (
            <div style={styles.container}>
                <div style={styles.header}>
                    <h1 style={styles.title}>Add Administrator</h1>
                    <p style={styles.subtitle}>Create a new administrator account</p>
                </div>
                <div style={{ ...styles.card, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
                    <div style={{ textAlign: 'center' }}>
                        <div style={styles.spinner} />
                        <p style={{ color: theme.textSecondary, marginTop: '16px' }}>Loading roles...</p>
                    </div>
                </div>
                <style>{`
          @keyframes spin {
            to { transform: rotate(360deg); }
          }
        `}</style>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

            {/* Loading Overlay */}
            {loading && (
                <div style={styles.loadingOverlay}>
                    <div style={styles.loadingCard}>
                        <div style={styles.spinner} />
                        <span style={{ color: theme.text, fontWeight: 500 }}>Adding administrator...</span>
                    </div>
                </div>
            )}

            <div style={styles.header}>
                <h1 style={styles.title}>Add Administrator</h1>
                <p style={styles.subtitle}>Create a new administrator account with specific roles and permissions</p>
            </div>

            {/* Notification */}
            {notification && (
                <div style={{
                    ...styles.notification,
                    background: notification.type === 'success' ? 'rgba(34, 197, 94, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                    border: `1px solid ${notification.type === 'success' ? '#22c55e' : '#ef4444'}`,
                    color: notification.type === 'success' ? '#22c55e' : '#ef4444',
                }}>
                    <span style={{ fontSize: '18px' }}>{notification.type === 'success' ? '✓' : '✕'}</span>
                    <span style={{ fontWeight: 500 }}>{notification.message}</span>
                </div>
            )}

            <div style={styles.card}>
                <form onSubmit={handleSubmit}>
                    <div style={styles.formGrid}>
                        {/* Username */}
                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Username<span style={styles.required}>*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Enter username"
                                value={formData.userName}
                                onChange={(e) => handleInputChange('userName', e.target.value)}
                                style={{
                                    ...styles.input,
                                    ...(errors.userName ? styles.inputError : {}),
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                onBlur={(e) => e.target.style.borderColor = errors.userName ? '#ef4444' : theme.inputBorder}
                            />
                            {errors.userName && <span style={styles.errorText}>{errors.userName}</span>}
                        </div>

                        {/* Email */}
                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Email Address<span style={styles.required}>*</span>
                            </label>
                            <input
                                type="email"
                                placeholder="Enter email address"
                                value={formData.emailAddress}
                                onChange={(e) => handleInputChange('emailAddress', e.target.value)}
                                style={{
                                    ...styles.input,
                                    ...(errors.emailAddress ? styles.inputError : {}),
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                onBlur={(e) => e.target.style.borderColor = errors.emailAddress ? '#ef4444' : theme.inputBorder}
                            />
                            {errors.emailAddress && <span style={styles.errorText}>{errors.emailAddress}</span>}
                        </div>

                        {/* First Name */}
                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                First Name<span style={styles.required}>*</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Enter first name"
                                value={formData.firstName}
                                onChange={(e) => handleInputChange('firstName', e.target.value)}
                                style={{
                                    ...styles.input,
                                    ...(errors.firstName ? styles.inputError : {}),
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                onBlur={(e) => e.target.style.borderColor = errors.firstName ? '#ef4444' : theme.inputBorder}
                            />
                            {errors.firstName && <span style={styles.errorText}>{errors.firstName}</span>}
                        </div>

                        {/* Last Name */}
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Last Name</label>
                            <input
                                type="text"
                                placeholder="Enter last name"
                                value={formData.lastName}
                                onChange={(e) => handleInputChange('lastName', e.target.value)}
                                style={styles.input}
                                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                            />
                        </div>

                        {/* Phone Number */}
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Phone Number</label>
                            <input
                                type="tel"
                                placeholder="Enter phone number"
                                value={formData.phoneNumber}
                                onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
                                style={styles.input}
                                onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                                onBlur={(e) => e.target.style.borderColor = theme.inputBorder}
                            />
                        </div>

                        {/* Admin Status */}
                        <div style={styles.formGroup}>
                            <label style={styles.label}>
                                Admin Status<span style={styles.required}>*</span>
                            </label>
                            <select
                                value={formData.adminStatus}
                                onChange={(e) => handleInputChange('adminStatus', e.target.value)}
                                style={{
                                    ...styles.select,
                                    ...(errors.adminStatus ? styles.inputError : {}),
                                }}
                            >
                                <option value="Active">Active</option>
                                <option value="Locked">Locked</option>
                                <option value="Disabled">Disabled</option>
                                <option value="UnderReview">Under Review</option>
                            </select>
                            {errors.adminStatus && <span style={styles.errorText}>{errors.adminStatus}</span>}
                        </div>

                        {/* Access Cards / Roles - Multi-select */}
                        <div style={styles.formGroupFull}>
                            <label style={styles.label}>
                                Access Cards / Roles<span style={styles.required}>*</span>
                            </label>
                            <div style={styles.multiSelectContainer}>
                                <div
                                    style={{
                                        ...styles.multiSelectTrigger,
                                        ...(errors.accessCards ? styles.inputError : {}),
                                    }}
                                    onClick={() => setRolesDropdownOpen(!rolesDropdownOpen)}
                                >
                                    {formData.accessCards.length === 0 ? (
                                        <span style={{ color: theme.textSecondary }}>Select roles...</span>
                                    ) : (
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', flex: 1 }}>
                                            {formData.accessCards.map(roleId => {
                                                const role = roles.find(r => r.id === roleId);
                                                return (
                                                    <span key={roleId} style={styles.selectedTag}>
                                                        {role?.name || roleId}
                                                        <span
                                                            style={styles.tagRemove}
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                toggleRole(roleId);
                                                            }}
                                                        >
                                                            ×
                                                        </span>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    )}
                                    <span style={{ fontSize: '12px', color: theme.textSecondary, transform: rolesDropdownOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
                                        ▼
                                    </span>
                                </div>

                                {rolesDropdownOpen && (
                                    <div style={styles.dropdown}>
                                        {roles.length === 0 ? (
                                            <div style={{ padding: '12px 14px', color: theme.textSecondary, textAlign: 'center' }}>
                                                No roles available
                                            </div>
                                        ) : (
                                            roles.filter(role => role.id).map(role => (
                                                <div
                                                    key={role.id}
                                                    style={styles.dropdownItem}
                                                    onClick={() => toggleRole(role.id)}
                                                    onMouseEnter={(e) => e.currentTarget.style.background = theme.tableRowHover}
                                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                                >
                                                    <div style={{
                                                        ...styles.checkbox,
                                                        ...(role.id && formData.accessCards.includes(role.id) ? styles.checkboxChecked : {}),
                                                    }}>
                                                        {role.id && formData.accessCards.includes(role.id) && (
                                                            <span style={{ color: '#fff', fontSize: '12px' }}>✓</span>
                                                        )}
                                                    </div>
                                                    <span style={{ color: theme.text }}>{role.name}</span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>
                            {errors.accessCards && <span style={styles.errorText}>{errors.accessCards}</span>}
                        </div>

                        {/* Is Senior Admin Toggle */}
                        <div style={styles.formGroupFull}>
                            <div style={styles.switchContainer}>
                                <div
                                    style={{
                                        ...styles.switch,
                                        background: formData.isSeniorAdmin ? '#2563eb' : '#475569',
                                    }}
                                    onClick={() => handleInputChange('isSeniorAdmin', !formData.isSeniorAdmin)}
                                >
                                    <div style={{
                                        ...styles.switchThumb,
                                        left: formData.isSeniorAdmin ? '24px' : '2px',
                                    }} />
                                </div>
                                <label style={styles.label}>Senior Administrator</label>
                            </div>
                            <p style={{ fontSize: '13px', color: theme.textSecondary, marginTop: '4px', marginLeft: '60px' }}>
                                Senior administrators have elevated privileges across the system
                            </p>
                        </div>
                    </div>

                    {/* Buttons */}
                    <div style={styles.buttonGroup}>
                        <button
                            type="submit"
                            style={styles.submitButton}
                            disabled={loading}
                            onMouseEnter={(e) => e.currentTarget.style.background = '#16a34a'}
                            onMouseLeave={(e) => e.currentTarget.style.background = '#22c55e'}
                        >
                            <span>+</span> Add Administrator
                        </button>
                        {onCancel && (
                            <button
                                type="button"
                                style={styles.cancelButton}
                                onClick={onCancel}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = theme.tableRowHover;
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                }}
                            >
                                Cancel
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdministratorDetailForm;